/**
 * one-stop/blocks-flow — the three flow-entry blocks.
 *
 *   join-queue · book-appointment · register-event
 *
 * Config only: each one names ONE subject (a queue, a service, an event) and
 * opens the same public flow the corresponding live block's CTA opens. They exist
 * so an author can put a single, prominent "Join the line at the OneStop desk" on
 * a page without listing every desk.
 *
 * VISUAL: the prototype's checklist card — white, `radius-default`, 14 px, title
 * over detail, and the brand-tinted pill action on the right — which is exactly
 * what a single named call-to-action looks like on that page. The details form is
 * a CHILD OF THE CARD, not of the title column, so it spans the card's width.
 *
 * ⚠ CONFIG KEYS ARE READ BY ALIAS, ON PURPOSE. `one-stop-builder`'s §4.3 table
 * generates the schemas for the nine milestone-1 types only; the flow-entry
 * schemas are in the registry but not in any document we hold. So each id is read
 * from the plausible alias set below and the block says so when it resolves
 * nothing, rather than guessing one spelling and rendering an empty card. Raised
 * in the milestone-2 reply: the §4.3 table needs extending to all 20 types.
 */
import { useCallback, useState } from "react";
import type { JSX } from "react";
import { Button } from "@v0y4ge/jrni-ui";
import { simulatedOpening, useCta, useOneStopActions } from "./actions";
import type { AppointmentHold, BookingConfirmation, EventRegistrationRef, QueueTicket } from "./actions";
import { configId, configText, formatInstantTime } from "./config";
import {
  Actions,
  CtaButton,
  CtaFeedback,
  Section,
  SuccessPanel,
  VisitorFields,
  useVisitor,
} from "./primitives";
import { uiStrings } from "./strings";
import type { Translate } from "./strings";
import type { BlockRenderProps } from "./types";

const QUEUE_ID_KEYS = ["queueId", "queue", "scope.queues", "subjectId", "targetId"] as const;
/* `preselects.serviceId` is the path the AUTHORING side actually writes — the
   inspector's row (`onestop-inspector.tsx`) and the ticked-service fan-out in
   `NewJourneys.tsx` (`OS_ITEM_FOR.service.idKey`) both put the id there. It was
   missing here, so an authored Book block resolved `serviceId: null` and fell
   through to its waiting state on every host: no handoff (that branch needs the
   id too) and no walk. The flat keys stay ahead of it for documents that carry
   one, and queue/event are authored flat, so only this family gains a path. */
const SERVICE_ID_KEYS = ["serviceId", "service", "preselects.serviceId", "scope.services", "subjectId", "targetId"] as const;
const EVENT_ID_KEYS = ["eventId", "event", "scope.events", "subjectId", "targetId"] as const;

/** The shared shell: one card, one action, every state visible. */
function FlowCard(props: {
  title: string;
  body: string | null;
  label: string;
  t: Translate;
  onClick: () => void;
  disabledReason: string | null;
  running: boolean;
  feedback: JSX.Element | null;
  /** Present only while the details form is open. */
  onCancel?: () => void;
  children?: JSX.Element | null;
}): JSX.Element {
  return (
    <Section>
      <div className="lv-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span className="lv-row-main">
            <span className="lv-row-title">{props.title}</span>
            {props.body ? <span className="lv-row-detail">{props.body}</span> : null}
          </span>
          <Actions>
            <CtaButton
              label={props.label}
              onClick={props.onClick}
              t={props.t}
              hierarchy="primary"
              disabledReason={props.disabledReason}
              running={props.running}
            />
            {props.onCancel ? (
              <span className="lv-act">
                <Button
                  variant="ghost"
                  size="small"
                  onClick={props.onCancel}
                  disabled={props.running}
                >
                  {props.t("visitor.cancel")}
                </Button>
              </span>
            ) : null}
          </Actions>
        </div>
        {props.children}
        {props.feedback}
      </div>
    </Section>
  );
}

/* ── join-queue ─────────────────────────────────────────────────── */

export function JoinQueueBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const actions = useOneStopActions();
  // Same operation as the desk card, so the same requirement: `email` optional.
  const visitor = useVisitor({ requireEmail: false });
  const [collecting, setCollecting] = useState(false);
  const [ticket, setTicket] = useState<QueueTicket | null>(null);
  const queueId = configId(block.config, QUEUE_ID_KEYS);

  const join = useCallback(
    () =>
      queueId
        ? actions.joinQueue({ queueId, visitor: visitor.value })
        : Promise.reject(new Error("")),
    [actions, queueId, visitor.value],
  );
  const cta = useCta(join, setTicket, visitor.showServerErrors);

  const reason = !queueId
    ? t("flow.unconfigured")
    : actions.available
      ? null
      : (actions.unavailableReason || t("cta.preview"));
  // Same progressive disclosure as the desk card: the card at rest is the
  // authored call to action, and the required fields appear on commit. The
  // button stays LIVE while they are empty — the fields say what is missing.
  const submit = (): void => {
    if (visitor.check()) cta.run();
  };

  /* HANDOFF: the card is a door into the walk-in journey, on this queue. It
     stops collecting a name and a phone when the host installed one — the
     journey asks for what it needs under its own published copy, and a front
     door that collected two fields and handed them nowhere asks twice. */
  if (actions.handoff && queueId) {
    const hand = actions.handoff;
    return (
      <FlowCard
        title={configText(block.config, "title", ctx.locale) ?? t("flow.join-queue.title")}
        body={configText(block.config, "subtitle", ctx.locale)}
        label={configText(block.config, "ctaLabel", ctx.locale) ?? t("flow.join-queue.cta")}
        t={t}
        onClick={() => hand({ kind: "queue", queueId })}
        disabledReason={null}
        running={false}
        /* — required by FlowCard, and NULL is the honest value on a handoff
           card (2026-09-08). The three handoff branches navigate into another
           journey; nothing runs here, so there is no outcome to report. The
           other FlowCards pass <CtaFeedback> because they own an async call. */
        feedback={null}
      />
    );
  }

  if (ticket) {
    return (
      <Section>
        <SuccessPanel
          eyebrow={ticket.recovered ? t("queue.already") : t("queue.joined.eyebrow")}
          headline={t("queue.joined.ticket", { ref: ticket.reference })}
          meta={ticket.checkInCode ? t("queue.joined.code", { code: ticket.checkInCode }) : null}
          aside={
            ticket.peopleAhead !== null
              ? { value: String(ticket.peopleAhead), label: t("queue.joined.ahead") }
              : null
          }
        />
      </Section>
    );
  }

  return (
    <FlowCard
      title={configText(block.config, "title", ctx.locale) ?? t("flow.join-queue.title")}
      body={configText(block.config, "subtitle", ctx.locale)}
      label={configText(block.config, "ctaLabel", ctx.locale) ?? t("flow.join-queue.cta")}
      t={t}
      onClick={collecting ? submit : () => setCollecting(true)}
      onCancel={collecting ? () => setCollecting(false) : undefined}
      disabledReason={reason}
      running={cta.state.status === "running"}
      feedback={<CtaFeedback t={t} disabledReason={reason} state={cta.state} />}
    >
      {collecting ? (
        <VisitorFields form={visitor} t={t} askPhone disabled={cta.state.status === "running"} />
      ) : null}
    </FlowCard>
  );
}

/* ── register-event ────────────────────────────────────────────── */

export function RegisterEventBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const actions = useOneStopActions();
  // The public event registration REQUIRES an email address.
  const visitor = useVisitor();
  const [collecting, setCollecting] = useState(false);
  const [registered, setRegistered] = useState<EventRegistrationRef | null>(null);
  const eventId = configId(block.config, EVENT_ID_KEYS);

  const register = useCallback(
    () =>
      eventId
        ? // A flow-entry block carries no fragment, so it has no resolved
          // registration state to read: it asks for a place, and the server's
          // answer says whether that was a seat or the waitlist.
          actions.registerForEvent({ eventId, visitor: visitor.value, waitlist: false })
        : Promise.reject(new Error("")),
    [actions, eventId, visitor.value],
  );
  const cta = useCta(register, setRegistered, visitor.showServerErrors);

  const reason = !eventId
    ? t("flow.unconfigured")
    : actions.available
      ? null
      : (actions.unavailableReason || t("cta.preview"));
  const submit = (): void => {
    if (visitor.check()) cta.run();
  };

  /* HANDOFF: into the events journey, on this event. Same reasoning as the
     event rows — that journey owns the tickets, the price and the consent. */
  if (actions.handoff && eventId) {
    const hand = actions.handoff;
    return (
      <FlowCard
        title={configText(block.config, "title", ctx.locale) ?? t("flow.register-event.title")}
        body={configText(block.config, "subtitle", ctx.locale)}
        label={configText(block.config, "ctaLabel", ctx.locale) ?? t("flow.register-event.cta")}
        t={t}
        onClick={() => hand({ kind: "event", eventId })}
        disabledReason={null}
        running={false}
        /* — required by FlowCard, and NULL is the honest value on a handoff
           card (2026-09-08). The three handoff branches navigate into another
           journey; nothing runs here, so there is no outcome to report. The
           other FlowCards pass <CtaFeedback> because they own an async call. */
        feedback={null}
      />
    );
  }

  /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): neutral already-registered state — no reference. CD to absorb. */
  if (registered?.outcome === "already") {
    return (
      <Section>
        <SuccessPanel
          eyebrow={t("event.alreadyRegistered")}
          headline={configText(block.config, "title", ctx.locale) ?? t("flow.register-event.title")}
          meta={t("event.alreadyRegistered.body")}
        />
      </Section>
    );
  }

  if (registered) {
    return (
      <Section>
        <SuccessPanel
          eyebrow={t(registered.outcome === "waitlist" ? "reg.waitlist" : "event.registered")}
          headline={configText(block.config, "title", ctx.locale) ?? t("flow.register-event.title")}
          meta={t("ref", { ref: registered.reference })}
        />
      </Section>
    );
  }

  return (
    <FlowCard
      title={configText(block.config, "title", ctx.locale) ?? t("flow.register-event.title")}
      body={configText(block.config, "subtitle", ctx.locale)}
      label={configText(block.config, "ctaLabel", ctx.locale) ?? t("flow.register-event.cta")}
      t={t}
      onClick={collecting ? submit : () => setCollecting(true)}
      onCancel={collecting ? () => setCollecting(false) : undefined}
      disabledReason={reason}
      running={cta.state.status === "running"}
      feedback={<CtaFeedback t={t} disabledReason={reason} state={cta.state} />}
    >
      {collecting ? (
        <VisitorFields form={visitor} t={t} disabled={cta.state.status === "running"} />
      ) : null}
    </FlowCard>
  );
}

/* ── book-appointment ─────────────────────────────────────────── */

/**
 * The one flow-entry block that still cannot complete inside a block, and the
 * reason is the domain, not the kit: a hold needs a SLOT, and this block's config
 * names only a service (plus optional `preselects`). Choosing a slot is the
 * booking funnel's own job — the `step` family (`time-slot-picker`,
 * `personal-details`, `confirmation`) which is
 * `allowed_document_kinds: ['journey']` and deliberately not in this renderer.
 *
 * The pieces now exist to do it here instead: `openingsOn` + `holdSlot` +
 * `checkoutHold`, driven from `config.preselects` (`serviceId` → `service_id`,
 * `staffId` → `staff_id`, `locationId` → **`facility_id`**, `dateWindow` →
 * `date_from`/`date_to`). That is the kit's §2.2d path, and §2.2d is explicit
 * that it is scaffolding rather than the preselect model (JAI-1161). It is also
 * unexercised: no document we hold places this block, so building the picker
 * against it would be the first render of untested UI on an authored surface.
 * Deliberately the second pass — named in the reply, not smuggled in.
 *
 * So this block renders its call to action and says what it is waiting for. It is
 * NOT a hidden button and NOT a dead one.
 *
 * UNDER SIMULATED ACTIONS IT DOES COMPLETE, and that is not the picker arriving
 * early. The simulation can answer "hold this service's next opening" without a
 * slot having been chosen by anybody, so the card takes ONE invented opening,
 * holds it, collects the three fields `checkout` requires and confirms — the
 * same three states the appointments row walks through. What is still missing
 * live is the CHOOSING, and a walkthrough that skips it is honest about that: an
 * operator reviewing this card sees the booking it will make, not a slot grid
 * that does not exist yet.
 */
export function BookAppointmentBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const actions = useOneStopActions();
  // `checkout` REQUIRES first name, last name and email — simulated or not.
  const visitor = useVisitor();
  const [hold, setHold] = useState<AppointmentHold | null>(null);
  const [booking, setBooking] = useState<BookingConfirmation | null>(null);
  const serviceId = configId(block.config, SERVICE_ID_KEYS);
  const walkable = actions.simulated === true && serviceId !== null;

  const take = useCallback(
    () =>
      walkable && serviceId
        ? actions.holdOpening({
            serviceId,
            opening: simulatedOpening(serviceId, null, ctx.timeZone),
          })
        : Promise.reject(new Error("")),
    [actions, walkable, serviceId, ctx.timeZone],
  );
  const holdCta = useCta(take, setHold);

  const confirm = useCallback(
    () =>
      hold
        ? actions.checkoutHold({ holdId: hold.holdId, visitor: visitor.value })
        : Promise.reject(new Error("")),
    [actions, hold, visitor.value],
  );
  const checkoutCta = useCta(confirm, setBooking, visitor.showServerErrors);

  const abandon = useCallback(() => {
    if (hold) void actions.releaseHold({ holdId: hold.holdId }).catch(() => undefined);
    setHold(null);
    holdCta.reset();
    checkoutCta.reset();
  }, [actions, hold, holdCta, checkoutCta]);

  const title = configText(block.config, "title", ctx.locale) ?? t("flow.book-appointment.title");

  /* HANDOFF, and this is the block it changes most. The reason this card could
     never complete is that a hold needs a SLOT and the config names only a
     service — slot choosing belongs to the journey's own `step` family. Handing
     off IS that missing step: the appointment journey opens on this service
     with its real availability, which is the flow this card was always a stub
     for. */
  if (actions.handoff && serviceId) {
    const hand = actions.handoff;
    return (
      <FlowCard
        title={title}
        body={configText(block.config, "subtitle", ctx.locale)}
        label={configText(block.config, "ctaLabel", ctx.locale) ?? t("flow.book-appointment.cta")}
        t={t}
        onClick={() => hand({ kind: "appointment", serviceId })}
        disabledReason={null}
        running={false}
        /* — required by FlowCard, and NULL is the honest value on a handoff
           card (2026-09-08). The three handoff branches navigate into another
           journey; nothing runs here, so there is no outcome to report. The
           other FlowCards pass <CtaFeedback> because they own an async call. */
        feedback={null}
      />
    );
  }

  if (booking) {
    return (
      <Section>
        <SuccessPanel
          eyebrow={t("book.confirmed")}
          headline={title}
          meta={t("ref", { ref: booking.reference })}
        />
      </Section>
    );
  }

  // Not simulated: the state this block has always had — the call to action, and
  // the sentence saying what it is waiting for.
  const reason = !serviceId
    ? t("flow.unconfigured")
    : walkable
      ? null
      : t("cta.pending.book");
  const running = holdCta.state.status === "running" || checkoutCta.state.status === "running";
  const submit = (): void => {
    if (visitor.check()) checkoutCta.run();
  };

  return (
    <FlowCard
      title={title}
      body={configText(block.config, "subtitle", ctx.locale)}
      label={hold ? t("book.confirm") : configText(block.config, "ctaLabel", ctx.locale) ?? t("flow.book-appointment.cta")}
      t={t}
      onClick={hold ? submit : walkable ? holdCta.run : () => undefined}
      onCancel={hold ? abandon : undefined}
      disabledReason={reason}
      running={running}
      feedback={
        reason && !walkable ? (
          <p className="lv-cta-reason">{reason}</p>
        ) : (
          <>
            {hold ? (
              <span className="lv-row-detail">
                {[
                  formatInstantTime(hold.startUtc, ctx.locale),
                  hold.expiresAt
                    ? t("book.holdUntil", { t: formatInstantTime(hold.expiresAt, ctx.locale) })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            ) : null}
            <CtaFeedback t={t} disabledReason={reason} state={holdCta.state} />
            <CtaFeedback t={t} disabledReason={null} state={checkoutCta.state} />
          </>
        )
      }
    >
      {hold ? <VisitorFields form={visitor} t={t} disabled={running} /> : null}
    </FlowCard>
  );
}
